/**
 * SegmentedControl — connected button group (radio-button replacement).
 * Single responsibility: multi-option toggle with green active state.
 */

import React from 'react';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  title?: string;
}

export interface SegmentedControlProps<T extends string> {
  label?: string;
  options: SegmentedOption<T>[];
  value: T | undefined;
  onChange: (value: T) => void;
  isDark: boolean;
}

/**
 * SegmentedControl component — connected button group.
 * Active button uses green accent background.
 */
export const SegmentedControl = <T extends string>({
  label,
  options,
  value,
  onChange,
  isDark,
}: SegmentedControlProps<T>): React.ReactElement => {
  const accent = isDark ? '#22c55e' : '#16a34a';
  const textSecondary = isDark ? '#94a3b8' : '#64748b';
  const textPrimary = isDark ? '#e2e8f0' : '#1e293b';
  const borderColor = isDark ? 'rgba(51,65,85,0.5)' : '#e2e8f0';
  const inactiveBg = isDark ? 'rgba(15,23,42,0.6)' : '#f8fafc';

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: textSecondary,
  };

  const groupStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0,
    borderRadius: 5,
    overflow: 'hidden',
    border: `1px solid ${borderColor}`,
    width: 'fit-content',
    maxWidth: '100%',
  };

  return (
    <div style={containerStyle}>
      {label && <span style={labelStyle}>{label}</span>}
      <div style={groupStyle} role="group" aria-label={label}>
        {options.map((opt, idx) => {
          const isActive = opt.value === value;
          const isFirst = idx === 0;

          const btnStyle: React.CSSProperties = {
            padding: '4px 8px',
            border: 'none',
            borderLeft: isFirst ? 'none' : `1px solid ${borderColor}`,
            background: isActive ? accent : inactiveBg,
            color: isActive ? '#ffffff' : textPrimary,
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: isActive ? 600 : 400,
            lineHeight: 1.4,
            transition: 'background 0.15s, color 0.15s',
            whiteSpace: 'nowrap',
          };

          return (
            <button
              key={opt.value}
              type="button"
              style={btnStyle}
              onClick={() => onChange(opt.value)}
              aria-pressed={isActive}
              aria-label={opt.title ?? opt.label}
              title={opt.title ?? opt.label}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

(SegmentedControl as React.FC).displayName = 'SegmentedControl';
